import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Map, GeoJSON, TileLayer } from 'react-leaflet'
import GeoJSONLabels from '../GeoJSONLabels/GeoJSONLabels'
import GeoFenceMapOverlay from '../GeoFenceMapOverlay'
import { geojsonIndia } from '../../util/methods'
import { DARK_TILE_MAX_ZOOM, DARK_TILE_URL, getLeafletTileConfig } from '../../util/mapTiles'

const MAA_GEOJSON_PATH = '/geo/maa.geojson'

function isDarkThemeActive() {
  return typeof document !== 'undefined' && !!document.body && document.body.classList.contains('dark-theme')
}

// Leaflet's animated flyTo() doesn't clamp the target zoom to maxZoom (setView does), so clamp it here
function clampFlyToZoom(map) {
  if (!map || map.__flyToClamped) return
  const originalFlyTo = map.flyTo
  map.flyTo = function (latlng, zoom, options) {
    const maxZoom = this.getMaxZoom()
    const clampedZoom = zoom !== undefined && zoom > maxZoom ? maxZoom : zoom
    return originalFlyTo.call(this, latlng, clampedZoom, options)
  }
  map.__flyToClamped = true
}

function shouldShowMaaByUrl() {
  if (typeof window === 'undefined') return false
  const hostname = (window.location && window.location.hostname) || ''
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const isAiaslSubdomain = hostname === 'aiasl' || hostname.startsWith('aiasl.')
  return isLocalhost || isAiaslSubdomain
}

const BaseMap = React.forwardRef(({
  center = [0, 0],
  zoom = 15,
  mapRef,
  onZoomEnd,
  attributionControl = false,
  className,
  style,
  tileLayerUrl,
  tileLayerMaxZoom = 22,
  tileLayerSubdomains,
  tileLayerAttribution,
  showIndiaGeoJson = true,
  showMaaGeoJson,
  indiaGeoJsonStyle = { color: 'grey', weight: 1 },
  maaGeoJsonStyle = { color: '#475569', weight: 1 },
  geoJsonLabelsZoom,
  geoJsonLabelsMinZoom = 17,
  geoJsonLabelsPath = '/geo/ahm.geojson',
  showGeoFenceOverlay = true,
  geoFenceOverlayOrganisationId,
  geoFenceOverlayAsset,
  geoFenceOverlayInteractive = false,
  geoFenceOverlayLabelMinZoom = 12,
  geoFenceOverlayHiddenCategories,
  geoFenceOverlayOnCategoriesLoaded,
  children,
  ...mapProps
}, ref) => {
  // Use the forwardRef ref if provided, otherwise use the mapRef prop for backward compatibility
  const resolvedRef = ref || mapRef
  const leafletRef = useRef(null)
  const setMapRef = useCallback((instance) => {
    leafletRef.current = instance
    if (instance) clampFlyToZoom(instance.leafletElement)
    if (typeof resolvedRef === 'function') resolvedRef(instance)
    else if (resolvedRef) resolvedRef.current = instance
  }, [resolvedRef])
  const [maaGeoJson, setMaaGeoJson] = useState(null)
  const [isDarkMode, setIsDarkMode] = useState(isDarkThemeActive)

  // Keep isDarkMode in sync with body class changes
  useEffect(() => {
    if (typeof document === 'undefined' || !document.body) return
    const observer = new MutationObserver(() => setIsDarkMode(isDarkThemeActive()))
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const showMaa = showMaaGeoJson !== undefined ? showMaaGeoJson : shouldShowMaaByUrl()

  useEffect(() => {
    if (!showMaa) return
    fetch(MAA_GEOJSON_PATH)
      .then((res) => res.json())
      .then((data) => setMaaGeoJson(data))
      .catch((err) => console.error('Failed to load maa.geojson:', err))
  }, [showMaa])

  const labelsZoom = geoJsonLabelsZoom !== undefined ? geoJsonLabelsZoom : zoom
  const labelsMinZoom = geoJsonLabelsMinZoom !== undefined ? geoJsonLabelsMinZoom : 17
  const defaultTileConfig = getLeafletTileConfig(isDarkMode)
  const resolvedTileLayerUrl = tileLayerUrl || defaultTileConfig.url
  const resolvedTileLayerSubdomains = tileLayerSubdomains !== undefined
    ? tileLayerSubdomains
    : defaultTileConfig.subdomains
  // Use custom attribution if it's explicitly provided AND not null/undefined
  // Otherwise use default attribution if not using custom tile URL
  const resolvedAttribution = tileLayerAttribution
    ? tileLayerAttribution // Use custom attribution if provided (not null/undefined)
    : (tileLayerUrl ? null : defaultTileConfig.attribution) // Use default if no custom URL

  // Dark tiles are capped at DARK_TILE_MAX_ZOOM; otherwise keep the caller's maxZoom (or Leaflet's layer-derived one)
  const isDarkTiles = resolvedTileLayerUrl === DARK_TILE_URL
  const baseMaxZoom = mapProps.maxZoom != null ? mapProps.maxZoom : tileLayerMaxZoom
  const effectiveMaxZoom = isDarkTiles ? Math.min(baseMaxZoom, DARK_TILE_MAX_ZOOM) : mapProps.maxZoom

  // react-leaflet v2 ignores maxZoom changes after mount, so apply theme/tile switches directly
  useEffect(() => {
    const map = leafletRef.current && leafletRef.current.leafletElement
    if (map) map.setMaxZoom(effectiveMaxZoom)
  }, [effectiveMaxZoom])

  return (
    <Map
      ref={setMapRef}
      center={center}
      zoom={zoom}
      attributionControl={true}
      onZoomend={onZoomEnd}
      className={className}
      style={style}
      {...mapProps}
      maxZoom={effectiveMaxZoom}
    >
      {/* Keyed by URL: react-leaflet v2 only calls setUrl() on change and keeps the old subdomains,
          which breaks `{s}` URLs when switching dark (no subdomains) -> light */}
      <TileLayer
        key={resolvedTileLayerUrl}
        attribution={resolvedAttribution || ''}
        maxZoom={tileLayerMaxZoom}
        url={resolvedTileLayerUrl}
        subdomains={resolvedTileLayerSubdomains}
      />
      {showIndiaGeoJson && (
        <GeoJSON data={geojsonIndia} style={indiaGeoJsonStyle} />
      )}
      {showMaa && maaGeoJson && (
        <GeoJSON data={maaGeoJson} style={maaGeoJsonStyle} />
      )}
      <GeoJSONLabels
        zoom={labelsZoom}
        minZoomLevel={labelsMinZoom}
        geoJSONPath={geoJsonLabelsPath}
      />
      {showGeoFenceOverlay && (
        <GeoFenceMapOverlay
          organisationId={geoFenceOverlayOrganisationId}
          asset={geoFenceOverlayAsset}
          interactive={geoFenceOverlayInteractive}
          mapZoom={zoom}
          labelMinZoom={geoFenceOverlayLabelMinZoom}
          hiddenCategories={geoFenceOverlayHiddenCategories}
          onCategoriesLoaded={geoFenceOverlayOnCategoriesLoaded}
        />
      )}
      {children}
    </Map>
  )
})

export default React.memo(BaseMap)
